import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { ArticlePath, createGuideLink } from '@mews-commander/enums';
import { WithEnterpriseId } from '@mews-commander/types';
import { lt } from '@mews/framework';
import { Banner, Link, Stack, Typography } from '@optimus-web/core';

interface UseGetIncompatibleIntegrationsBannerParams extends WithEnterpriseId {
    integrationId?: string;
}

export const useGetIncompatibleIntegrationsBanner = ({
    enterpriseId,
    integrationId,
}: UseGetIncompatibleIntegrationsBannerParams) => {
    const guideLink = createGuideLink(ArticlePath.IncorrectPaymentAndCancellationPoliciesDisplay);

    const { data: integrationsData } = useRepositorySuspenseQuery('integrations', 'getAll', { enterpriseId });

    const { data } = useRepositorySuspenseQuery(
        'integrations',
        'getPolicyExceptionsIncompatibleIntegrations',
        {
            enterpriseId,
        },
        {
            select: ({ policyExceptionsIncompatibleIntegrationClientIds, enterpriseHasPolicyExceptions }) => ({
                integrations:
                    integrationsData?.integrations?.filter(
                        ({ clientId }) =>
                            clientId &&
                            policyExceptionsIncompatibleIntegrationClientIds.includes(clientId) &&
                            (integrationId ? clientId === integrationId : true)
                    ) || [],
                hasExceptions: enterpriseHasPolicyExceptions,
            }),
        }
    );

    const banner = (
        <Stack vertical spacing="400">
            {data.integrations?.map(({ name, id }) => (
                <Banner
                    key={id}
                    primaryText={lt('IntegrationNotCompatibleWithPolicyExceptionsBannerTitle', {
                        integrationName: name ?? id,
                    })}
                    secondaryText={
                        <Stack vertical spacing="100">
                            {lt('IntegrationNotCompatibleWithPolicyExceptionsBannerDescription')}
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {lt('IntegrationNotCompatibleWithPolicyExceptionsBannerAricleLink', {
                                    a: (text) => (
                                        <Link href={guideLink} target="_blank" rel="noreferrer" variant="basic">
                                            {text}
                                        </Link>
                                    ),
                                })}
                            </Typography>
                        </Stack>
                    }
                    variant="warning"
                />
            ))}
        </Stack>
    );

    return {
        banner,
        data: data ?? [],
    };
};

interface Props {
    integrationId?: string;
}
export const IncompatibleIntegrationsBanner = ({ integrationId }: Props) => {
    const enterpriseId = useCurrentEnterpriseId();
    const { banner } = useGetIncompatibleIntegrationsBanner({ enterpriseId, integrationId });
    return banner;
};
